import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Plus, LogIn, LogOut, Trash2, AlertTriangle, RefreshCw, Repeat, Send, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CareFlagsPills from "@/components/CareFlagsPills";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";

function todayStr() { return new Date().toISOString().slice(0, 10); }

function StatusPill({ status }) {
  const map = {
    scheduled:   "bg-slate-100 text-slate-700 border-slate-300",
    checked_in:  "bg-sky-100 text-sky-800 border-sky-300",
    completed:   "bg-emerald-100 text-emerald-800 border-emerald-300",
    missed:      "bg-red-100 text-red-800 border-red-300",
  };
  return (
    <span data-testid={`visit-status-${status}`} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${map[status] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
      {(status || "scheduled").replace("_", " ")}
    </span>
  );
}

export default function Rota() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("day");
  const [date, setDate] = useState(todayStr());
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [form, setForm] = useState({
    client_id: "", staff_user_id: "", scheduled_start: "", scheduled_end: "", purpose: "", notes: "",
  });
  const [tForm, setTForm] = useState({
    client_id: "", staff_user_id: "", weekday: "0", start_time: "09:00", end_time: "", purpose: "", notes: "",
  });
  const [genRange, setGenRange] = useState({ start_date: todayStr(), end_date: "" });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishRange, setPublishRange] = useState({ start_date: todayStr(), end_date: "" });
  const [safety, setSafety] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, c, t, s, sf] = await Promise.all([
        api.get(`/visits?date=${date}`),
        api.get("/clients"),
        api.get("/rota/templates"),
        api.get("/auth/users").catch(() => ({ data: [] })),
        api.get("/dashboard/client-safety").catch(() => ({ data: { clients: {} } })),
      ]);
      setVisits(v.data || []);
      setClients(c.data || []);
      setTemplates(t.data || []);
      setStaff((s.data || []).filter((u) => u.role === "care_worker" || u.role === "manager"));
      setSafety(sf.data?.clients || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.client_id || !form.scheduled_start) {
      toast.error("Service user and start time are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, staff_user_id: form.staff_user_id || null };
      await api.post("/visits", { data: payload });
      toast.success("Visit scheduled");
      setOpen(false);
      setForm({ client_id: "", staff_user_id: "", scheduled_start: "", scheduled_end: "", purpose: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/visits/${id}/${action}`);
      toast.success(action === "check-in" ? "Checked in" : "Checked out");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this visit?")) return;
    try {
      await api.delete(`/visits/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitTemplate = async () => {
    if (!tForm.client_id || !tForm.start_time) {
      toast.error("Service user and start time are required");
      return;
    }
    try {
      await api.post("/rota/templates", { data: { ...tForm, staff_user_id: tForm.staff_user_id || null, weekday: Number(tForm.weekday) } });
      toast.success("Template saved");
      setTemplateOpen(false);
      setTForm({ client_id: "", staff_user_id: "", weekday: "0", start_time: "09:00", end_time: "", purpose: "", notes: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this recurring template?")) return;
    try {
      await api.delete(`/rota/templates/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generate = async () => {
    if (!genRange.start_date || !genRange.end_date) {
      toast.error("Pick a start and end date");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/rota/generate", genRange);
      toast.success(`Generated ${r.data.created} visit(s), skipped ${r.data.skipped} duplicate(s)`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setGenerating(false); }
  };

  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-slate-200 bg-white px-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Care Delivery</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="rota-title">Rota &amp; Visits</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTab("day")} data-testid="tab-day" className={`h-9 px-3 text-sm border ${tab === "day" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>Day view</button>
          <button onClick={() => setTab("templates")} data-testid="tab-templates" className={`h-9 px-3 text-sm border inline-flex items-center gap-1.5 ${tab === "templates" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>
            <Repeat className="w-3.5 h-3.5" /> Weekly templates
          </button>
        </div>
      </div>

      {tab === "day" ? (
        <>
        <div className="h-12 border-b border-slate-200 bg-slate-50 px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-500" />
            <input
              type="date" value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="rota-date"
              className="h-9 border border-slate-300 bg-white px-2 text-sm"
            />
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)} data-testid="btn-add-visit" className="h-9 bg-slate-900 hover:bg-slate-800">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add visit
            </Button>
          )}
        </div>

      <div className="p-6">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="rota-loading">Loading visits…</div>
        ) : visits.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="rota-empty">
            No visits scheduled for {date}.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="rota-list">
            {visits.map((v) => (
              <li key={v.id} data-testid={`visit-${v.id}`} className="bg-white border border-slate-200 p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-slate-900">{v.client_name || v.client_id}</div>
                    <StatusPill status={v.status} />
                    {v.from_template_id && (
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> recurring</span>
                    )}
                    {v.published_at && v.acceptance_status === "accepted" && (
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-emerald-50 border-emerald-300 text-emerald-800" data-testid={`visit-accepted-${v.id}`}>Accepted</span>
                    )}
                    {v.published_at && v.acceptance_status === "declined" && (
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-red-50 border-red-300 text-red-800" data-testid={`visit-declined-${v.id}`}>Declined</span>
                    )}
                    {v.published_at && (!v.acceptance_status || v.acceptance_status === "pending") && (
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-amber-50 border-amber-300 text-amber-800" data-testid={`visit-pending-${v.id}`}>Awaiting accept</span>
                    )}
                    {asArray(v.client_allergies).slice(0, 2).map((a) => (
                      <span key={a} className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full border ${ALLERGY_SEVERITY_STYLES[getAllergySeverity(a)]}`}>
                        <AlertTriangle className="w-3 h-3" /> {a}
                      </span>
                    ))}
                    {asArray(v.client_allergies).length > 2 && (
                      <span className="text-[11px] text-slate-500">+{asArray(v.client_allergies).length - 2}</span>
                    )}
                  </div>
                  {(safety[v.client_id]?.critical_allergies?.length > 0) && (
                    <div className="mt-1 text-[11px] text-red-800 bg-red-50 border-l-2 border-red-500 px-2 py-1" data-testid={`rota-critical-allergy-${v.id}`}>
                      <AlertTriangle className="w-3 h-3 inline-block mr-1" />
                      {safety[v.client_id].critical_allergies.map((a, i) => (
                        <span key={i}>{i > 0 ? " · " : ""}<span className="font-semibold uppercase text-[10px] tracking-widest">{a.severity}</span> {a.item}{a.reaction ? ` — ${a.reaction}` : ""}</span>
                      ))}
                    </div>
                  )}
                  {(safety[v.client_id]?.care_flags?.length > 0) && (
                    <div className="mt-1"><CareFlagsPills flags={safety[v.client_id].care_flags} size="sm" /></div>
                  )}
                  <div className="text-xs text-slate-600 mt-1">
                    {new Date(v.scheduled_start).toLocaleString()}{v.scheduled_end ? ` → ${new Date(v.scheduled_end).toLocaleTimeString()}` : ""}
                    {v.purpose ? ` · ${v.purpose}` : ""}
                  </div>
                  {v.staff_name ? (
                    <div className="text-xs text-sky-700 mt-1" data-testid={`visit-staff-${v.id}`}>Assigned to {v.staff_name}</div>
                  ) : (
                    <div className="text-xs text-amber-700 mt-1" data-testid={`visit-unassigned-${v.id}`}>Unassigned</div>
                  )}
                  {v.notes && <div className="text-xs text-slate-500 mt-1">{v.notes}</div>}
                  {v.check_in_at && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Checked in {new Date(v.check_in_at).toLocaleTimeString()} by {v.checked_in_by_name || v.checked_in_by}
                      {v.check_out_at ? ` · Checked out ${new Date(v.check_out_at).toLocaleTimeString()}` : ""}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {v.published_at && v.acceptance_status !== "accepted" && v.staff_user_id === user?.id && (
                    <>
                      <Button size="sm" variant="outline" className="h-8 border-emerald-300 text-emerald-800"
                        onClick={async () => {
                          try { await api.post(`/visits/${v.id}/accept`); toast.success("Shift accepted"); await load(); }
                          catch (e) { toast.error(formatApiError(e)); }
                        }}
                        data-testid={`btn-accept-${v.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Accept
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 border-red-300 text-red-700"
                        onClick={async () => {
                          if (!window.confirm("Decline this shift? Your manager will be notified so it can be reassigned.")) return;
                          try { await api.post(`/visits/${v.id}/decline`); toast.success("Shift declined — manager notified"); await load(); }
                          catch (e) { toast.error(formatApiError(e)); }
                        }}
                        data-testid={`btn-decline-${v.id}`}
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Decline
                      </Button>
                    </>
                  )}
                  {v.published_at && v.status !== "completed" && !v.check_in_at && (
                    <Button size="sm" variant="outline" onClick={() => doAction(v.id, "check-in")} data-testid={`btn-check-in-${v.id}`}>
                      <LogIn className="w-3.5 h-3.5 mr-1.5" /> Check in
                    </Button>
                  )}
                  {v.published_at && v.check_in_at && v.status !== "completed" && (
                    <Button size="sm" onClick={() => doAction(v.id, "check-out")} data-testid={`btn-check-out-${v.id}`} className="bg-emerald-700 hover:bg-emerald-800">
                      <LogOut className="w-3.5 h-3.5 mr-1.5" /> Check out
                    </Button>
                  )}
                  {canManage && (
                    <Button size="sm" variant="ghost" onClick={() => doDelete(v.id)} data-testid={`btn-delete-visit-${v.id}`} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      </>
      ) : (
        <div className="p-6 space-y-4">
          <div className="bg-white border border-slate-200 p-4 flex flex-col md:flex-row md:items-end gap-3" data-testid="templates-generator">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Generate visits from active templates</div>
              <div className="text-sm text-slate-700">Pick a date range. We&apos;ll create a visit for every active weekly template on matching weekdays and skip anything already scheduled.</div>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
                <Input type="date" value={genRange.start_date} onChange={(e) => setGenRange({ ...genRange, start_date: e.target.value })} data-testid="gen-start" className="h-9" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
                <Input type="date" value={genRange.end_date} onChange={(e) => setGenRange({ ...genRange, end_date: e.target.value })} data-testid="gen-end" className="h-9" />
              </div>
              <Button size="sm" onClick={generate} disabled={generating || !canManage} data-testid="btn-generate-rota" className="h-9 bg-emerald-700 hover:bg-emerald-800">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${generating ? "animate-spin" : ""}`} /> Generate
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-4 flex flex-col md:flex-row md:items-end gap-3" data-testid="publish-week-panel">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Publish week to carers</div>
              <div className="text-sm text-slate-700">Marks every scheduled shift in the range as <span className="font-medium">published</span> and sends each assigned carer a push notification with an accept button.</div>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
                <Input type="date" value={publishRange.start_date} onChange={(e) => setPublishRange({ ...publishRange, start_date: e.target.value })} data-testid="pub-start" className="h-9" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
                <Input type="date" value={publishRange.end_date} onChange={(e) => setPublishRange({ ...publishRange, end_date: e.target.value })} data-testid="pub-end" className="h-9" />
              </div>
              <Button
                size="sm"
                onClick={async () => {
                  if (!publishRange.end_date) { toast.error("Pick a range"); return; }
                  setPublishing(true);
                  try {
                    const r = await api.post("/rota/publish", publishRange);
                    const d = r.data;
                    const parts = [];
                    if (d.newly_published_count) parts.push(`${d.newly_published_count} new`);
                    if (d.republished_count) parts.push(`${d.republished_count} re-published`);
                    if (d.already_accepted_count) parts.push(`${d.already_accepted_count} already accepted (not re-pinged)`);
                    if (d.unassigned_count) parts.push(`${d.unassigned_count} unassigned`);
                    toast.success(
                      `Published ${d.published_count} shift(s) · notified ${d.carers_notified} carer(s) — ${parts.join(" · ") || "no diff"}`,
                      { duration: 6000 },
                    );
                    await load();
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setPublishing(false); }
                }}
                disabled={publishing || !canManage}
                data-testid="btn-publish-week"
                className="h-9 bg-slate-900 hover:bg-slate-800"
              >
                <Send className={`w-3.5 h-3.5 mr-1.5 ${publishing ? "animate-pulse" : ""}`} /> Publish
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Active templates</div>
            {canManage && (
              <Button size="sm" onClick={() => setTemplateOpen(true)} data-testid="btn-add-template" className="h-9 bg-slate-900 hover:bg-slate-800">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New template
              </Button>
            )}
          </div>

          {templates.length === 0 ? (
            <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="templates-empty">
              No recurring templates yet.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="templates-list">
              {templates.map((t) => (
                <li key={t.id} data-testid={`template-${t.id}`} className="bg-white border border-slate-200 p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900">{t.client_name || t.client_id}</div>
                    <div className="text-xs text-slate-600 mt-1">
                      Every {WEEKDAYS[t.weekday]} · {t.start_time}{t.end_time ? ` → ${t.end_time}` : ""}
                      {t.purpose ? ` · ${t.purpose}` : ""}
                    </div>
                    {t.notes && <div className="text-xs text-slate-500 mt-1">{t.notes}</div>}
                  </div>
                  {canManage && (
                    <Button size="sm" variant="ghost" onClick={() => deleteTemplate(t.id)} data-testid={`btn-delete-template-${t.id}`} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="visit-dialog">
          <DialogHeader>
            <DialogTitle>Schedule a visit</DialogTitle>
            <DialogDescription>Pick a service user and time slot. Staff can check in when they arrive.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Service user</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="visit-client"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign staff (optional)</Label>
              <Select value={form.staff_user_id || "unassigned"} onValueChange={(v) => setForm({ ...form, staff_user_id: v === "unassigned" ? "" : v })}>
                <SelectTrigger data-testid="visit-staff"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email} · {s.role.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start</Label>
                <Input type="datetime-local" value={form.scheduled_start} onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })} data-testid="visit-start" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="datetime-local" value={form.scheduled_end} onChange={(e) => setForm({ ...form, scheduled_end: e.target.value })} data-testid="visit-end" />
              </div>
            </div>
            <div>
              <Label>Purpose</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Morning personal care" data-testid="visit-purpose" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="visit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-save-visit" className="bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving…" : "Schedule visit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>New weekly template</DialogTitle>
            <DialogDescription>Create a recurring visit slot. Use Generate to turn templates into concrete visits for a date range.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Service user</Label>
              <Select value={tForm.client_id} onValueChange={(v) => setTForm({ ...tForm, client_id: v })}>
                <SelectTrigger data-testid="template-client"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default staff (optional)</Label>
              <Select value={tForm.staff_user_id || "unassigned"} onValueChange={(v) => setTForm({ ...tForm, staff_user_id: v === "unassigned" ? "" : v })}>
                <SelectTrigger data-testid="template-staff"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Weekday</Label>
                <Select value={tForm.weekday} onValueChange={(v) => setTForm({ ...tForm, weekday: v })}>
                  <SelectTrigger data-testid="template-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start</Label>
                <Input type="time" value={tForm.start_time} onChange={(e) => setTForm({ ...tForm, start_time: e.target.value })} data-testid="template-start-time" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" value={tForm.end_time} onChange={(e) => setTForm({ ...tForm, end_time: e.target.value })} data-testid="template-end-time" />
              </div>
            </div>
            <div>
              <Label>Purpose</Label>
              <Input value={tForm.purpose} onChange={(e) => setTForm({ ...tForm, purpose: e.target.value })} placeholder="e.g. Morning personal care" data-testid="template-purpose" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={tForm.notes} onChange={(e) => setTForm({ ...tForm, notes: e.target.value })} data-testid="template-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateOpen(false)}>Cancel</Button>
            <Button onClick={submitTemplate} data-testid="btn-save-template" className="bg-slate-900 hover:bg-slate-800">
              Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
